import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { TEuDdpOrder } from 'redux/reducers/euDdp/types';
import { getOmnivaLabelRequest, searchOrdersRequest } from 'redux/reducers/euDdp/reducer';
import { selectOrdersSearch, selectOrdersSearchLoading } from 'redux/reducers/euDdp/selectors';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { EU_DDP_ACTIONS } from './constants';
import { getTrackingPageUrl } from './utils';

export const EuDdpOrders: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const ordersData = useSelector(selectOrdersSearch);
	const ordersLoading = useSelector(selectOrdersSearchLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEuDdpOrder>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const handleLink = useCallback(
		(id: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(generatePath(PATHS.EU_DDP_ORDER_DETAIL_PAGE, { orderId: String(id) }));
		},
		[dispatch, navigate],
	);

	useEffect(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		if (params['f-id'])
			filters.push({
				fieldName: 'id',
				value: params['f-id'] as string,
				operator: '=',
			});
		if (params['f-externalOrderId'])
			filters.push({
				fieldName: 'externalOrderId',
				value: params['f-externalOrderId'] as string,
				operator: '=',
			});

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchOrdersRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleAction = useCallback(
		(id: number, type: string) => {
			const order = ordersData?.data?.find((item) => item.id === id);
			if (!order) return;
			if (type === 'Get Omniva label') {
				dispatch(
					getOmnivaLabelRequest({
						orderId: order.easOrderId,
						fileName: `omniva_label_${order.easOrderId}.pdf`,
					}),
				);
			} else if (type === EU_DDP_ACTIONS.TRACKING) {
				if (order.firstMileTrackingNumber)
					window.open(getTrackingPageUrl(order.firstMileTrackingNumber), '_blank');
				return;
			}
		},
		[dispatch, ordersData],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TEuDdpOrder>
				tableStructure={currentTableStructure}
				items={ordersData}
				itemsIsLoading={ordersLoading}
				handleAction={handleAction}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						EU DDP Orders
					</Typography>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => window.open('https://maasikas.emta.ee/', '_blank')}
						>
							Visit eMTA
						</Button>
						<Button
							variant="contained"
							color="primary"
							size="small"
							onClick={() => window.open(PATHS.EU_DDP_PRINT_LABELS_PAGE, '_blank')}
						>
							Print labels list
						</Button>
					</Box>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
